import React, { useCallback, useEffect, useRef, useMemo, memo } from 'react';
import styled from 'styled-components';
import { TranscriptElementProps, TranscriptEditorParagraphProps } from './interfaces';
import TranscriptElement from './TranscriptElement';
import { ElementOperationManager } from '../managers/ElementOperationManager';
import { TextElement } from '../models/TranscriptElement';
import { defaultTheme } from './theme';

/**
 * Component for editing a transcript paragraph
 * Preserves element structure while providing a natural editing experience
 */
const TranscriptEditorParagraph: React.FC<TranscriptEditorParagraphProps> = memo(({
  paragraph,
  segmentId,
  editorStore,
  selectionManager,
  onElementClick,
  searchTerm = '',
  counter
}) => {
  // Reference to the paragraph container
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Operations manager for batch operations
  const operationManager = useRef(new ElementOperationManager());
  
  // Cache DOM element references for better performance
  const elementRefsMap = useRef(new Map<string, HTMLElement>());
  
  // Keep a reference to paragraph data for event handlers
  const paragraphRef = useRef(paragraph);
  
  // Update reference when paragraph changes
  useEffect(() => {
    paragraphRef.current = paragraph;
  }, [paragraph]);
  
  // Element lookup helper - faster than querySelector
  const getElementNode = useCallback((elementId: string): HTMLElement | null => {
    // Try the cached reference first
    if (elementRefsMap.current.has(elementId)) {
      return elementRefsMap.current.get(elementId) || null;
    }
    
    // Fall back to querySelector if needed
    if (containerRef.current) {
      const element = containerRef.current.querySelector(`[data-element-id="${elementId}"]`) as HTMLElement;
      if (element) {
        elementRefsMap.current.set(elementId, element);
        return element;
      }
    }
    
    return null;
  }, []);
  
  // Handle element text changes
  const handleElementTextChange = useCallback((elementId: string, newText: string, immediate: boolean = false) => {
    // Find the element by ID
    const element = paragraphRef.current.elements.find(el => el.id === elementId);
    if (!element) return;
    
    // Check if text has actually changed
    if (newText === element.value) return;
    
    // Check if there's a multi-element selection
    const selectedElements = selectionManager?.getSelectionData();
    const hasMultiElementSelection = selectedElements && selectedElements.length > 1;
    
    if (hasMultiElementSelection) {
      // Multi-element selection case (e.g., word join operation)
      const elementIndices = selectedElements && selectedElements
        .map((el: any) => paragraphRef.current.elements.findIndex(pe => pe.id === el.id))
        .filter((idx: number) => idx !== -1);
      
      if (elementIndices.length > 1) {
        const startIdx = Math.min(...elementIndices);
        const endIdx = Math.max(...elementIndices);
        
        // Create a single new element replacing the selection
        const updatedElements = [...paragraphRef.current.elements];
        
        // Create a proper element using the factory
        const { TranscriptElementFactory } = require('../models/TranscriptElementFactory');
        
        // Generate element using the factory, preserving the type and other properties
        const newElement = element.type === 'text'
          ? TranscriptElementFactory.createTextElement(
              element.id,
              newText,
              (element as any).startTime,
              (element as any).endTime,
              (element as any).confidence
            )
          : TranscriptElementFactory.createPunctElement(
              element.id,
              newText
            );
        
        // Merge timing if these are text elements
        if (element.type === 'text') {
          const lastElement = paragraphRef.current.elements[endIdx];
          if (lastElement && lastElement.type === 'text') {
            const textElement = element as TextElement;
            const textLastElement = lastElement as TextElement;
            const textNewElement = newElement as TextElement;
            textNewElement.startTime = textElement.startTime;
            textNewElement.endTime = textLastElement.endTime;
          }
        }
        
        // Replace first element with our new combined element
        updatedElements[startIdx] = newElement;
        
        // Remove all other elements in the selection range
        if (endIdx > startIdx) {
          updatedElements.splice(startIdx + 1, endIdx - startIdx);
        }
        
        // Update the full paragraph
        editorStore.updateParagraphElements(
          segmentId,
          paragraph.id,
          updatedElements
        );
      } else {
        // Single element edit with selection - standard case
        if (immediate) {
          editorStore.updateElementText(
            segmentId,
            paragraph.id,
            elementId,
            newText
          );
        } else {
          editorStore.updateElementTextDebounced(
            segmentId,
            paragraph.id,
            elementId,
            newText,
            editorStore.currentUserId || 'user-id',
            false
          );
        }
      }
    } else {
      // Regular single element edit
      if (immediate) {
        editorStore.updateElementText(
          segmentId,
          paragraph.id,
          elementId,
          newText
        );
      } else {
        editorStore.updateElementTextDebounced(
          segmentId,
          paragraph.id,
          elementId,
          newText,
          editorStore.currentUserId || 'user-id',
          false
        );
      }
    }
  }, [paragraph.id, segmentId, editorStore, selectionManager]);
  
  // Handle keyboard interactions
  const handleKeyDown = useCallback((e: React.KeyboardEvent, elementId: string) => {
    // Get the element index
    const elementIndex = paragraphRef.current.elements.findIndex(el => el.id === elementId);
    if (elementIndex === -1) return;
    
    const element = paragraphRef.current.elements[elementIndex];
    if (!element) return;
    
    // Get the current target
    const target = e.currentTarget as HTMLElement;
    
    // Handle different key presses
    switch (e.key) {
      case 'ArrowLeft': {
        // Handle left arrow at beginning of element
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;
        
        // Check if at start of element
        const range = selection.getRangeAt(0);
        const elementRange = document.createRange();
        elementRange.selectNodeContents(target);
        elementRange.collapse(true); // Collapse to start
        
        const isAtStart = range.compareBoundaryPoints(Range.START_TO_START, elementRange) === 0;
        
        if (isAtStart) {
          // Move to previous element if at start
          if (elementIndex > 0) {
            e.preventDefault();
            const prevElement = paragraphRef.current.elements[elementIndex - 1];
            const prevElementNode = getElementNode(prevElement.id);
            
            if (prevElementNode) {
              prevElementNode.focus();
              // Position cursor at end of previous element
              const textNode = prevElementNode.firstChild || prevElementNode;
              const range = document.createRange();
              
              if (textNode.nodeType === Node.TEXT_NODE) {
                range.setStart(textNode, textNode.textContent?.length || 0);
              } else {
                range.selectNodeContents(prevElementNode);
                range.collapse(false); // Collapse to end
              }
              
              selection.removeAllRanges();
              selection.addRange(range);
            }
          } else {
            // At first element in paragraph, notify parent
            const event = new CustomEvent('navigate-prev-paragraph', {
              detail: { segmentId, paragraphId: paragraph.id },
              bubbles: true
            });
            containerRef.current?.dispatchEvent(event);
          }
        }
        break;
      }
      
      case 'ArrowRight': {
        // Handle right arrow at end of element
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;
        
        // Check if at end of element
        const range = selection.getRangeAt(0);
        const elementRange = document.createRange();
        elementRange.selectNodeContents(target);
        elementRange.collapse(false); // Collapse to end
        
        const isAtEnd = range.compareBoundaryPoints(Range.END_TO_END, elementRange) === 0;
        
        if (isAtEnd) {
          // Move to next element if at end
          if (elementIndex < paragraphRef.current.elements.length - 1) {
            e.preventDefault();
            const nextElement = paragraphRef.current.elements[elementIndex + 1];
            const nextElementNode = getElementNode(nextElement.id);
            
            if (nextElementNode) {
              nextElementNode.focus();
              // Position cursor at start of next element
              const range = document.createRange();
              const textNode = nextElementNode.firstChild || nextElementNode;
              
              if (textNode.nodeType === Node.TEXT_NODE) {
                range.setStart(textNode, 0);
              } else {
                range.selectNodeContents(nextElementNode);
                range.collapse(true); // Collapse to start
              }
              
              selection.removeAllRanges();
              selection.addRange(range);
            }
          } else {
            // At last element in paragraph, notify parent
            const event = new CustomEvent('navigate-next-paragraph', {
              detail: { segmentId, paragraphId: paragraph.id },
              bubbles: true
            });
            containerRef.current?.dispatchEvent(event);
          }
        }
        break;
      }
      
      case 'Backspace': {
        // Handle backspace at beginning of element
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;
        
        // Check if at start of element
        const range = selection.getRangeAt(0);
        const elementRange = document.createRange();
        elementRange.selectNodeContents(target);
        elementRange.collapse(true); // Collapse to start
        
        const isAtStart = range.compareBoundaryPoints(Range.START_TO_START, elementRange) === 0;
        
        // Check for selections
        const selectedElements = selectionManager?.getSelectionData();
        const hasMultiElementSelection = selectedElements && selectedElements.length > 1;
        
        if (hasMultiElementSelection) {
          // Handle multi-element selection delete
          e.preventDefault();
          
          // Get indices of selected elements
          const elementIndices = selectedElements && selectedElements
            .map((el: any) => paragraphRef.current.elements.findIndex(pe => pe.id === el.id))
            .filter((idx: number) => idx !== -1) || [];
          
          if (elementIndices.length > 0) {
            const startIdx = Math.min(...elementIndices);
            const endIdx = Math.max(...elementIndices);
            
            // Create a proper element using the factory
            const { TranscriptElementFactory } = require('../models/TranscriptElementFactory');
            const sourceElement = paragraphRef.current.elements[startIdx];
            const elementId = `element_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
            
            // Create element using factory
            const newElement = sourceElement.type === 'text'
              ? TranscriptElementFactory.createTextElement(
                  elementId,
                  '', // Empty value
                  (sourceElement as any).startTime,
                  (sourceElement as any).endTime,
                  (sourceElement as any).confidence
                )
              : TranscriptElementFactory.createPunctElement(
                  elementId,
                  '' // Empty value
                );
            
            // Update timing if these are text elements
            const firstElement = paragraphRef.current.elements[startIdx];
            const lastElement = paragraphRef.current.elements[endIdx];
            
            if (firstElement.type === 'text' && lastElement.type === 'text') {
              const textFirstElement = firstElement as TextElement;
              const textLastElement = lastElement as TextElement;
              const textNewElement = newElement as TextElement;
              textNewElement.startTime = textFirstElement.startTime;
              textNewElement.endTime = textLastElement.endTime;
            }
            
            // Create updated elements array
            const updatedElements = [...paragraphRef.current.elements];
            updatedElements[startIdx] = newElement;
            
            // Remove other elements in selection
            if (endIdx > startIdx) {
              updatedElements.splice(startIdx + 1, endIdx - startIdx);
            }
            
            // Update paragraph
            editorStore.updateParagraphElements(
              segmentId,
              paragraph.id,
              updatedElements
            );
            
            // Clear selection in manager
            selectionManager?.clearSelection();
          }
        } else if (isAtStart) {
          e.preventDefault();
          
          // If at first element of paragraph, merge with previous paragraph
          if (elementIndex === 0) {
            // Notify parent to handle cross-paragraph merge
            const event = new CustomEvent('navigate-prev-paragraph-end', {
              detail: {
                segmentId,
                paragraphId: paragraph.id,
                elementId,
                deleteCurrentElement: true
              },
              bubbles: true
            });
            containerRef.current?.dispatchEvent(event);
            return;
          }
          
          // Standard case: merge with previous element in same paragraph
          const prevElement = paragraphRef.current.elements[elementIndex - 1];
          
          // Create a deep clone of the previous element before modifying
          const prevElementClone = JSON.parse(JSON.stringify(prevElement));
          const mergedElement = operationManager.current.mergeElements(prevElementClone, element);
          
          // Update elements array
          const updatedElements = [...paragraphRef.current.elements];
          updatedElements[elementIndex - 1] = mergedElement;
          updatedElements.splice(elementIndex, 1);
          
          // Save important data for cursor restoration before updating
          const mergePosition = prevElement.value?.length || 0;
          const mergedElementId = mergedElement.id;
          
          // Update paragraph
          editorStore.updateParagraphElements(
            segmentId,
            paragraph.id,
            updatedElements
          );
          
          // Set cursor at merge point using requestAnimationFrame for reliable timing
          requestAnimationFrame(() => {
            const mergedNode = getElementNode(mergedElementId);
            
            if (mergedNode) {
              try {
                const range = document.createRange();
                const selection = window.getSelection();
                
                // Set cursor at the merge point (where the two elements joined)
                const textNode = mergedNode.firstChild || mergedNode;
                
                if (textNode.nodeType === Node.TEXT_NODE) {
                  range.setStart(textNode, Math.min(mergePosition, textNode.textContent?.length || 0));
                } else {
                  range.selectNodeContents(mergedNode);
                  range.collapse(false); // Place at end
                }
                
                selection?.removeAllRanges();
                selection?.addRange(range);
                mergedNode.focus();
              } catch (error) {
                console.error('Error setting selection after backspace merge:', error);
              }
            }
          });
        }
        break;
      }
      
      case 'Delete': {
        // Handle delete at end of element
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;
        
        // Check if at end of element
        const range = selection.getRangeAt(0);
        const elementRange = document.createRange();
        elementRange.selectNodeContents(target);
        elementRange.collapse(false); // Collapse to end
        
        const isAtEnd = range.compareBoundaryPoints(Range.END_TO_END, elementRange) === 0;
        
        if (isAtEnd && elementIndex < paragraphRef.current.elements.length - 1) {
          e.preventDefault();
          
          // Get next element
          const nextElement = paragraphRef.current.elements[elementIndex + 1];
          
          // Merge this element with next one
          const updatedElements = [...paragraphRef.current.elements];
          
          // Create merged element - do a deep clone first
          const elementClone = JSON.parse(JSON.stringify(element));
          const mergedElement = operationManager.current.mergeElements(elementClone, nextElement);
          
          // Update current element
          updatedElements[elementIndex] = mergedElement;
          
          // Remove next element
          updatedElements.splice(elementIndex + 1, 1);
          
          // Save data for cursor restoration
          const mergePosition = element.value?.length || 0;
          const mergedElementId = mergedElement.id;
          
          // Update paragraph
          editorStore.updateParagraphElements(
            segmentId,
            paragraph.id,
            updatedElements
          );
          
          // Set selection to merge point
          requestAnimationFrame(() => {
            const mergedElementNode = getElementNode(mergedElementId);
            
            if (mergedElementNode) {
              try {
                const range = document.createRange();
                const selection = window.getSelection();
                
                // Set cursor at the merge point (end of first element text)
                const textNode = mergedElementNode.firstChild || mergedElementNode;
                
                if (textNode.nodeType === Node.TEXT_NODE) {
                  range.setStart(textNode, Math.min(mergePosition, textNode.textContent?.length || 0));
                } else {
                  range.selectNodeContents(mergedElementNode);
                  range.collapse(false);
                }
                
                selection?.removeAllRanges();
                selection?.addRange(range);
                mergedElementNode.focus();
              } catch (error) {
                console.error('Error setting selection after delete merge:', error);
              }
            }
          });
        }
        break;
      }
      
      case 'Enter': {
        // Handle Enter key (split element)
        e.preventDefault();
        
        // Get cursor position
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;
        
        const cursorPos = selection.anchorOffset;
        
        // Split the element
        const [firstPart, secondPart] = operationManager.current.splitElement(
          element, 
          cursorPos
        );
        
        // Create new elements array
        const updatedElements = [...paragraphRef.current.elements];
        updatedElements[elementIndex] = firstPart;
        updatedElements.splice(elementIndex + 1, 0, secondPart);
        
        // Save ID for cursor restoration
        const secondPartId = secondPart.id;
        
        // Update paragraph
        editorStore.updateParagraphElements(
          segmentId,
          paragraph.id,
          updatedElements
        );
        
        // Focus second part after update
        requestAnimationFrame(() => {
          const secondElement = getElementNode(secondPartId);
          
          if (secondElement) {
            secondElement.focus();
            // Place cursor at beginning
            const range = document.createRange();
            range.setStart(secondElement.firstChild || secondElement, 0);
            range.collapse(true);
            
            const selection = window.getSelection();
            selection?.removeAllRanges();
            selection?.addRange(range);
          }
        });
        break;
      }
    }
  }, [segmentId, paragraph.id, editorStore, getElementNode, selectionManager]);
  
  // Handle paste events
  const handlePaste = useCallback((e: React.ClipboardEvent, elementId: string) => {
    e.preventDefault();
    
    // Get pasted text
    const pastedText = e.clipboardData.getData('text/plain');
    if (!pastedText) return;
    
    // Check for selected elements (multi-element selections)
    const selectedElements = selectionManager?.getSelectionData();
    const hasMultiElementSelection = selectedElements && selectedElements.length > 1;
    
    // Get element info
    const elementIndex = paragraphRef.current.elements.findIndex(el => el.id === elementId);
    if (elementIndex === -1) return;
    
    const element = paragraphRef.current.elements[elementIndex];
    if (!element) return;
    
    // Handle paste with selection - replace multiple elements with pasted text
    if (hasMultiElementSelection) {
      // Convert selection data to element indices
      const elementIndices = selectedElements!
        .map((el: any) => paragraphRef.current.elements.findIndex(pe => pe.id === el.id))
        .filter((idx: number) => idx !== -1);
      
      if (elementIndices.length > 0) {
        const startIdx = Math.min(...elementIndices);
        const endIdx = Math.max(...elementIndices);
        
        // Create tokens from pasted text
        const pastedTokens = operationManager.current.tokenizeText(pastedText);
        
        if (pastedTokens.length === 0) return;
        
        // Create new elements from tokens
        const newElements = pastedTokens.map((token: string) => {
          const isPunct = operationManager.current.isPunctuation(token);
          
          // Generate a unique element ID
          const elementId = `element_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
          
          // Use the factory to create proper elements
          const { TranscriptElementFactory } = require('../models/TranscriptElementFactory');
          
          if (isPunct) {
            // Create punct element using factory
            return TranscriptElementFactory.createPunctElement(elementId, token);
          } else {
            // Create text element using factory
            const startTime = paragraphRef.current.elements[startIdx].type === 'text' 
              ? (paragraphRef.current.elements[startIdx] as TextElement).startTime 
              : undefined;
            const endTime = paragraphRef.current.elements[endIdx].type === 'text'
              ? (paragraphRef.current.elements[endIdx] as TextElement).endTime
              : undefined;
              
            return TranscriptElementFactory.createTextElement(
              elementId,
              token,
              startTime,
              endTime,
              1.0 // confidence
            );
          }
        });
        
        // Create updated elements array
        const updatedElements = [...paragraphRef.current.elements];
        
        // Replace selected elements with new ones
        updatedElements.splice(startIdx, endIdx - startIdx + 1, ...newElements);
        
        // Save the first element ID for cursor restoration
        const firstNewElementId = newElements[0].id;
        
        // Update paragraph
        editorStore.updateParagraphElements(
          segmentId,
          paragraph.id,
          updatedElements
        );
        
        // Focus the first new element
        requestAnimationFrame(() => {
          const firstElement = getElementNode(firstNewElementId);
          
          if (firstElement) {
            firstElement.focus();
            // Place cursor at beginning
            const range = document.createRange();
            range.setStart(firstElement.firstChild || firstElement, 0);
            range.collapse(true);
            
            const selection = window.getSelection();
            selection?.removeAllRanges();
            selection?.addRange(range);
          }
        });
        
        return;
      }
    }
    
    // Single element paste - get cursor position
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    
    const cursorPos = selection.anchorOffset;
    
    // Process paste
    const newElements = operationManager.current.handlePaste(
      element,
      pastedText,
      cursorPos
    );
    
    // Update elements array
    const updatedElements = [...paragraphRef.current.elements];
    updatedElements.splice(elementIndex, 1, ...newElements);
    
    // Update paragraph
    editorStore.updateParagraphElements(
      segmentId,
      paragraph.id,
      updatedElements
    );
  }, [segmentId, paragraph.id, editorStore, getElementNode, selectionManager]);
  
  // Clear element reference cache when elements change
  useEffect(() => {
    // Only keep references that still exist in paragraph.elements
    const currentIds = new Set(paragraph.elements.map(el => el.id));
    const keysToRemove: string[] = [];
    
    elementRefsMap.current.forEach((_, key) => {
      if (!currentIds.has(key)) {
        keysToRemove.push(key);
      }
    });
    
    keysToRemove.forEach(key => {
      elementRefsMap.current.delete(key);
    });
  }, [paragraph.elements]);
  
  // Create a stable key that changes when paragraph structure changes
  const paragraphVersionKey = useMemo(() => {
    return `${paragraph.id}-${paragraph.elements.map(e => e.id).join('-')}`;
  }, [paragraph.id, paragraph.elements, counter]);
  
  // Render paragraph elements
  const renderElements = useCallback(() => {
    return paragraph.elements.map((element, index) => {
      const isLastInParagraph = index === paragraph.elements.length - 1;
      const nextElement = index < paragraph.elements.length - 1 ? paragraph.elements[index + 1] : null;
      const isFollowedByPunctuation = nextElement?.type === 'punct';
      
      return (
        <TranscriptElement
          key={element.id}
          element={element}
          segmentId={segmentId}
          paragraphId={paragraph.id}
          index={index}
          isLastInParagraph={isLastInParagraph}
          isFollowedByPunctuation={isFollowedByPunctuation}
          onTextChange={handleElementTextChange}
          onClick={onElementClick}
          onKeyDown={handleKeyDown}
          searchTerm={searchTerm}
          counter={element.counter}
        />
      );
    });
  }, [
    paragraph.elements, 
    paragraph.id, 
    segmentId, 
    handleElementTextChange, 
    handleKeyDown, 
    onElementClick,
    searchTerm
  ]);
  
  return (
    <EditorContainer
      key={paragraphVersionKey}
      ref={containerRef}
      onKeyDown={e => e.stopPropagation()}
      onPaste={(e) => {
        const target = e.target as HTMLElement;
        const elementId = target.getAttribute('data-element-id');
        if (elementId) {
          handlePaste(e, elementId);
        }
      }}
      data-paragraph-id={paragraph.id}
      data-segment-id={segmentId}
    >
      {renderElements()}
    </EditorContainer>
  );
}, (prevProps, nextProps) => {
  if (prevProps.counter !== nextProps.counter) return false;
  if (prevProps.paragraph !== nextProps.paragraph) return false;
  if (prevProps.paragraph.counter !== nextProps.paragraph.counter) return false;
  if (prevProps.paragraph.elements.length !== nextProps.paragraph.elements.length) return false;
  if (prevProps.segmentId !== nextProps.segmentId) return false;
  if (prevProps.searchTerm !== nextProps.searchTerm) return false;
  
  // Check if any element values have changed
  for (let i = 0; i < prevProps.paragraph.elements.length; i++) {
    const prevEl = prevProps.paragraph.elements[i];
    const nextEl = nextProps.paragraph.elements[i];
    if (prevEl.value !== nextEl.value) return false;
  }
  
  return true;
});

TranscriptEditorParagraph.displayName = 'TranscriptEditorParagraph';

export default TranscriptEditorParagraph;

/**
 * Editor container for paragraph content
 */
export const EditorContainer = styled.div`
  display: block;
  line-height: 175%;
  color: ${defaultTheme.colors.text};
  padding: ${defaultTheme.spacing.xs} ${defaultTheme.spacing.sm};
  white-space: pre-wrap;
  word-break: break-word;
  position: relative;
  font-size: ${defaultTheme.fontSizes.regular};
  border-radius: ${defaultTheme.borderRadius.sm};
  transition: background-color 0.15s ease;
  
  /* Make it more document-like */
  &:focus-within {
    outline: none;
    background-color: rgba(255, 255, 255, 0.03);
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.05);
  }
  
  /* Hide timestamp IDs from being selected in text */
  *[data-start-time]::before,
  *[data-end-time]::before {
    content: attr(data-start-time) " - " attr(data-end-time);
    display: none;
  }
  
  /* Make cursor more visible */
  caret-color: ${defaultTheme.colors.text};
  
  /* Important for nested contentEditable */
  &:focus {
    outline: none;
  }
  
  /* For Google Docs-like selection appearance */
  &::selection {
    background-color: transparent;
    color: ${defaultTheme.colors.text};
  }
`;

